import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useCreatePaymentIntent } from 'hooks/useStripePayments';
import { useAuthContext } from 'context/authContext';
import { supabase } from 'lib/supabase';
import Payment from './Payment';
import PropTypes from 'prop-types';
import paths from 'routes/paths';
import PNDonationSummary from './donation-form-components/PNDonationSummary';
import { Card, Col, Row } from 'react-bootstrap';
import Section from 'components/common/Section';

function DonationPaymentPage({ data }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [error, setError] = useState(null);

  const { createPaymentIntentAsync } = useCreatePaymentIntent();
  const { organization, selectedBranchId } = useAuthContext();

  useEffect(() => {
    const initializePayment = async () => {
      try {
        const { donorId, total } = data;

        // Build order details for metadata (webhook will use this to create order)
        const orderDetails = data.donationItems.map(
          ({
            amount,
            quantity = 1,
            values,
            type,
            packageId,
            donationLocationIds
          }) => ({
            amount: amount * quantity,
            billing_condition: values.billingCondition,
            donation_type_id: type?.id || type,
            package_id: packageId,
            location_id_list: donationLocationIds?.length
              ? donationLocationIds
              : null
          })
        );

        const subtotalAmount = parseFloat(data.subtotal);
        const transactionFeesAmount = data.settings?.coverTransactionFee
          ? parseFloat(data.transactionFees)
          : 0;
        const giftAidEligible = data.settings?.giftAid || false;
        const giftAidData = data.settings?.giftAid ? data.giftAidData : null;

        const isRecurring = orderDetails.some(item => item.billing_condition === 'monthly');

        // Create payment intent with order data in metadata
        const response = await createPaymentIntentAsync({
          donorId: donorId,
          amount: parseFloat(total),
          orderDetails,
          subtotal: subtotalAmount,
          transactionFees: transactionFeesAmount,
          giftAidEligible,
          giftAidData,
          isRecurring
        });

        if (!response?.client_secret || !response?.publishable_key) {
          setError('Failed to initialize payment');
          toast.error('Failed to initialize payment');
          return;
        }

        // Create basket session BEFORE exposing the payment form — the Stripe
        // webhook reads the order details from this session to create the order.
        // If this fails and we let the user pay, the charge succeeds but no
        // donation is ever recorded.
        const basket = data.donationItems.map(item => ({
          id: crypto.randomUUID(),
          count: item.quantity || 1,
          packageId: item.packageId || null,
          packageName: item.name || '',
          branchId: selectedBranchId,
          transactionFee: 0,
          donationType: item.type,
          donationAmount: {
            value: item.amount,
            label: `${item.currencySymbol || '£'}${item.amount}`
          },
          paymentType: item.values?.billingCondition || 'one-off'
        }));

        const { error: basketError } = await supabase.functions.invoke(
          'create-basket-session',
          {
            body: {
              donorId: response.donor_id || donorId,
              basket,
              subtotal: subtotalAmount,
              transactionFee: transactionFeesAmount,
              totalAmount: parseFloat(total),
              branchId: selectedBranchId,
              organisationId: organization?.id,
              giftAidEligible,
              giftAidData,
              orderDetails,
              paymentIntentId: response.payment_intent_id
            }
          }
        );

        if (basketError) {
          const errorBody = basketError.context
            ? await basketError.context.json().catch(() => null)
            : null;
          console.error(
            'Basket session error:',
            basketError,
            'Response:',
            errorBody
          );
          setError('Failed to prepare donation. Please try again.');
          toast.error('Failed to prepare donation. Please try again.');
          return;
        }

        setClientSecret(response.client_secret);
        setPublishableKey(response.publishable_key);
      } catch (err) {
        setError(err.message || 'Something went wrong');
        toast.error('Something went wrong');
        console.log(err);
      }
    };

    if (data.donorId && data.total) {
      initializePayment();
    }
  }, [data, createPaymentIntentAsync, selectedBranchId, organization]);

  const baseUrl = window.location.origin;

  const { donationItems, subtotal, transactionFees, total } = data || {};

  return (
    <Section className="py-0">
      <Row className="g-3">
        <Col md={8} className="order-1 order-md-0">
          {clientSecret && publishableKey ? (
            <Payment
              clientSecret={clientSecret}
              publishableKey={publishableKey}
              redirectUrl={`${baseUrl}${paths.paymentSuccess}`}
            />
          ) : error ? (
            <Card>
              <Card.Body className="text-center py-5">
                <h5 className="text-danger">Payment Setup Failed</h5>
                <p className="text-muted">{error}</p>
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Body className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5>Processing your donation...</h5>
                <p className="text-muted">
                  Please wait while we set up your payment
                </p>
              </Card.Body>
            </Card>
          )}
        </Col>
        <Col md={4} className="order-0 order-md-1">
          {data && (
            <PNDonationSummary
              items={donationItems}
              subtotal={subtotal}
              transactionFees={transactionFees}
              total={total}
            />
          )}
        </Col>
      </Row>
    </Section>
  );
}

DonationPaymentPage.propTypes = {
  data: PropTypes.object.isRequired
};

export { DonationPaymentPage };
